import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '@/utils/toast.ts';
import {
  getImportMapping,
  ImportMappingResponse,
} from '@/services/importMappingsApi.ts';
import { getCsvFiles } from '@/services/csvFilesApi.ts';
import type { StudyForm } from '@/types/studyForms';
import type { CsvFile } from '@/types/csvFiles';
import {
  transformMappingData,
  type MappingData,
} from '../utils/transformMappingData';

interface UseImportMappingDataOptions {
  mappingId: string;
  studyFormsMap: Map<number, StudyForm>;
}

interface UseImportMappingDataResult {
  mappingData: MappingData | null;
  csvFiles: Map<number, CsvFile>;
  loading: boolean;
  error: string | null;
  isRefreshing: boolean;
  isPolling: boolean;
  loadMappingData: (isRefresh?: boolean) => Promise<void>;
}

export function useImportMappingData({
  mappingId,
  studyFormsMap,
}: UseImportMappingDataOptions): UseImportMappingDataResult {
  const [mappingData, setMappingData] = useState<MappingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [csvFiles, setCsvFiles] = useState<Map<number, CsvFile>>(new Map());
  const [loadingAdditionalData, setLoadingAdditionalData] = useState(false);
  void loadingAdditionalData;
  const initialLoadRef = useRef(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentStatusRef = useRef<string | null>(null);
  const apiResponseRef = useRef<ImportMappingResponse | null>(null);

  // Load additional data (forms and files)
  const loadAdditionalData = useCallback(
    async (mappingResponse: ImportMappingResponse) => {
      try {
        setLoadingAdditionalData(true);

        // Extract unique form IDs and file IDs from mappings
        const formIds = new Set<number>();
        const fileIds = new Set<number>();

        if (mappingResponse.file_to_form_mappings) {
          mappingResponse.file_to_form_mappings.forEach(ftf => {
            if (ftf.study_form_id) {
              formIds.add(ftf.study_form_id);
            }
            if (ftf.csv_file_id) {
              fileIds.add(ftf.csv_file_id);
            }
          });
        }

        if (mappingResponse.column_mappings) {
          mappingResponse.column_mappings.forEach(cm => {
            if (cm.study_form_id) {
              formIds.add(cm.study_form_id);
            }
            if (cm.csv_file_id) {
              fileIds.add(cm.csv_file_id);
            }
          });
        }

        // Extract form IDs from import_validation_errors
        if (mappingResponse.import_validation_errors) {
          Object.keys(mappingResponse.import_validation_errors).forEach(
            formIdStr => {
              const formId = parseInt(formIdStr, 10);
              if (!isNaN(formId)) {
                formIds.add(formId);
              }
            }
          );
        }

        // Load CSV files
        if (fileIds.size > 0) {
          try {
            const filesResponse = await getCsvFiles({ per_page: 1000 });
            const filesMap = new Map<number, CsvFile>();
            filesResponse.data.forEach(file => {
              if (fileIds.has(file.id)) {
                filesMap.set(file.id, file);
              }
            });
            setCsvFiles(filesMap);
            console.log('✅ Loaded CSV files:', filesMap.size);
          } catch (err) {
            console.warn('⚠️ Failed to load CSV files:', err);
          }
        }
      } catch (err) {
        console.warn('⚠️ Error loading additional data:', err);
      } finally {
        setLoadingAdditionalData(false);
      }
    },
    []
  );

  // Load mapping data
  const loadMappingData = useCallback(
    async (isRefresh = false) => {
      console.log('🔄 Loading real mapping data from API');

      try {
        setError(null);
        if (isRefresh) {
          setIsRefreshing(true);
        } else {
          setLoading(true);
        }

        const apiResponse = await getImportMapping(mappingId);
        console.log('✅ Real API call successful');

        // Store API response for re-transformation
        apiResponseRef.current = apiResponse;

        // Load additional data (forms and files) - this will update state
        await loadAdditionalData(apiResponse);

        // Transform data to component format - will be updated by useEffect when forms/files load
        const transformedData = transformMappingData(
          apiResponse,
          new Map(),
          new Map()
        );
        setMappingData(transformedData);
        currentStatusRef.current = transformedData.status;

        if (isRefresh) {
          setIsRefreshing(false);
        } else {
          setLoading(false);
        }
      } catch (err: unknown) {
        console.error('❌ Error loading mapping data:', err);

        // Handle specific error types
        const errorWithStatus = err as { status?: number };
        if (errorWithStatus?.status === 404) {
          setError('Import mapping not found');
          toast.error('Import mapping not found or access denied');
        } else if (errorWithStatus?.status === 403) {
          setError('Access denied to this import mapping');
          toast.error('You do not have permission to view this mapping');
        } else {
          setError('Failed to load mapping details');
          toast.error('Failed to load mapping details. Please try again.');
        }

        if (isRefresh) {
          setIsRefreshing(false);
        } else {
          setLoading(false);
        }
      }
    },
    [mappingId, loadAdditionalData]
  );

  // Polling function that doesn't set isRefreshing
  const pollMappingData = useCallback(async () => {
    // Check current status before polling
    const currentStatus = currentStatusRef.current;
    if (currentStatus !== 'created' && currentStatus !== 'valid_for_import') {
      // Stop polling if status changed
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setIsPolling(false);
        console.log(
          `🛑 Stopping polling - status changed to: ${currentStatus}`
        );
      }
      return;
    }

    try {
      setIsPolling(true);
      const apiResponse = await getImportMapping(mappingId);
      console.log('🔄 Polling: API call successful');

      // Store API response for re-transformation
      apiResponseRef.current = apiResponse;

      // Load additional data (forms and files) - this will update state
      await loadAdditionalData(apiResponse);

      // Transform data to component format - will be updated by useEffect when forms/files load
      const transformedData = transformMappingData(
        apiResponse,
        new Map(),
        new Map()
      );
      setMappingData(transformedData);
      currentStatusRef.current = transformedData.status;
      setIsPolling(false);
    } catch (err: unknown) {
      console.error('❌ Error polling mapping data:', err);
      setIsPolling(false);
      // Don't show error toast for polling failures, just log
    }
  }, [mappingId, loadAdditionalData]);

  // Update mapping data when forms or files are loaded
  useEffect(() => {
    if (
      apiResponseRef.current &&
      (studyFormsMap.size > 0 || csvFiles.size > 0)
    ) {
      // Re-transform data with updated forms and files maps
      const transformedData = transformMappingData(
        apiResponseRef.current,
        studyFormsMap,
        csvFiles
      );
      setMappingData(transformedData);
      currentStatusRef.current = transformedData.status;
    }
  }, [studyFormsMap, csvFiles]);

  // Update status ref when mappingData changes
  useEffect(() => {
    if (mappingData) {
      currentStatusRef.current = mappingData.status;
    }
  }, [mappingData]);

  // Load data only once when component mounts with valid mappingId
  useEffect(() => {
    // Only run once when component mounts
    if (!initialLoadRef.current && mappingId) {
      console.log('🏗️ Initial load for mappingId:', mappingId);
      initialLoadRef.current = true;
      loadMappingData(false);
    }
  }, [loadMappingData, mappingId]);

  // Auto-polling for created and valid_for_import statuses
  useEffect(() => {
    // Clear existing interval if any
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      setIsPolling(false);
    }

    if (!mappingData) {
      return;
    }

    const status = mappingData.status;
    const shouldPoll = status === 'created' || status === 'valid_for_import';

    if (!shouldPoll) {
      setIsPolling(false);
      return;
    }

    // Don't poll if manual refresh is in progress
    if (isRefreshing) {
      return;
    }

    console.log(`🔄 Starting polling for status: ${status}`);
    pollingIntervalRef.current = setInterval(() => {
      pollMappingData();
    }, 6000);

    return () => {
      if (pollingIntervalRef.current) {
        console.log('🛑 Stopping polling (cleanup)');
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setIsPolling(false);
      }
    };
  }, [mappingData, isRefreshing, pollMappingData]);

  return {
    mappingData,
    csvFiles,
    loading,
    error,
    isRefreshing,
    isPolling,
    loadMappingData,
  };
}

